import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from './supabase';

/**
 * Undoing a delete.
 *
 * Deletion sets `deleted_at` rather than removing the row, so restoring is just
 * clearing that stamp. Without this the soft delete would be a promise the
 * product could not keep: the record survives in the database, but nobody
 * without SQL access could ever get it back.
 *
 * The audit log keeps both events, so a delete followed by a restore reads as
 * exactly that rather than as if nothing happened.
 */

/** Only tables that carry `deleted_at`. */
export type RestorableTable =
  | 'clients' | 'projects' | 'suppliers' | 'subcontractors'
  | 'materials' | 'bank_accounts' | 'subcontractor_contracts';

const AFFECTED: Record<RestorableTable, string[]> = {
  clients:        ['clients', 'client', 'client-options', 'dashboard'],
  projects:       ['projects', 'project', 'client', 'dashboard', 'lookups'],
  suppliers:      ['suppliers', 'lookups'],
  subcontractors: ['subcontractors', 'lookups'],
  materials:      ['materials', 'lookups'],
  bank_accounts:  ['bank-accounts'],
  subcontractor_contracts: ['subcontractor-contracts', 'subcontractors', 'lookups'],
};

export function useRestore() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ table, id }: { table: RestorableTable; id: string }) => {
      const { error } = await (supabase.from as unknown as (t: string) => {
        update: (v: Record<string, unknown>) => {
          eq: (c: string, v: string) => PromiseLike<{ error: { message: string } | null }>;
        };
      })(table).update({ deleted_at: null }).eq('id', id);
      if (error) throw new Error(error.message);
    },
    onSuccess: (_data, { table }) => {
      for (const key of AFFECTED[table]) {
        void qc.invalidateQueries({ queryKey: [key] });
      }
    },
  });
}
